import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Entity } from '@app/core/model/entities/entity';
import { FormatType } from '@app/core/model/other/field-config';
import { getValue } from '@app/shared/extra/utils';
import { Aggregation, Filter, FilterKind, NumberFilterType } from '@app/shared/grid/datasource-utils';
import { IStatusPanelAngularComp } from 'ag-grid-angular';
import { GridApi, IStatusPanelParams } from 'ag-grid-community';
import { combineLatest, fromEventPattern, Subject } from 'rxjs';

export interface IIndicatorWidget {
  // Styling
  label: string;
  icon: string;
  formatType: FormatType;
  suffix?: string;

  // Computation
  aggregation: Aggregation;
  filters: Filter[];

  // Display
  totalValue: number;
  filteredValue: number;
}

@Component({
  selector: 'indicator-status-panel',
  templateUrl: './indicator-status-panel.component.html',
  styleUrls: ['./indicator-status-panel.component.scss'],
  standalone: false
})
export class IndicatorStatusPanelComponent implements IStatusPanelAngularComp {

  public gridApi: GridApi<Entity>;
  private indicatorsSet$ = new Subject<void>();
  protected isAnyFilterPresent = signal(false);

  public indicators: IIndicatorWidget[] = [];

  private destroyRef = inject(DestroyRef);

  /**
   * Initializes the indicator status panel component as part of the ag-Grid status panel interface.
   * @param params params provided by Ag-Grid on initialisation
   */
  public agInit(params: IStatusPanelParams<Entity>): void {
    this.gridApi = params.api;

    combineLatest([
      fromEventPattern(
        (handler) => this.gridApi.addEventListener('rowDataUpdated', handler),
        (handler) => { if (!this.gridApi.isDestroyed()) this.gridApi.removeEventListener('rowDataUpdated', handler); }
      ),
      this.indicatorsSet$.asObservable()
    ]).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.initIndicators();
        this.calculateIndicatorFilters();
      });

    fromEventPattern(
      (handler) => this.gridApi.addEventListener('filterChanged', handler),
      (handler) => { if (!this.gridApi.isDestroyed()) this.gridApi.removeEventListener('filterChanged', handler); }
    ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.calculateIndicatorFilters();
        this.isAnyFilterPresent.set(this.gridApi.isAnyFilterPresent());
      });
  }

  /**
   * Configures the list of indicator widgets to be displayed and calculated in the status panel.
   * @param indicators indicator configurations
   */
  public setIndicators(indicators: IIndicatorWidget[]): void {
    this.indicators = indicators;
    this.indicatorsSet$.next();
  }

  /**
   * Calculates the total values for all indicators based on the complete dataset.
   */
  public initIndicators(): void {
    this.clearInitValues();
    this.gridApi.forEachNode((node) => {
      if (!node.group) {
        const rowData = node.data;

        this.indicators.forEach((indicator) => {
          if (this.evalFilter(rowData, indicator.filters)) {
            if (indicator.aggregation.operation == 'COUNT') {
              indicator.totalValue += 1;
            } else if (indicator.aggregation.operation == 'SUM') {
              indicator.totalValue += getValue(rowData, indicator.aggregation.field.split('.')) ?? 0;
            }
          }
        });
      }
    });
  }

  /**
   * Calculates the filtered values for all indicators based on the currently applied grid filters.
   */
  public calculateIndicatorFilters(): void {
    this.clearFilteredValues();
    this.gridApi.forEachNodeAfterFilter(node => {
      if (!node.group) {
        const rowData = node.data;

        this.indicators.forEach((indicator) => {
          if (this.evalFilter(rowData, indicator.filters)) {
            if (indicator.aggregation.operation == 'COUNT') {
              indicator.filteredValue += 1;
            } else if (indicator.aggregation.operation == 'SUM') {
              indicator.filteredValue += getValue(rowData, indicator.aggregation.field.split('.')) ?? 0;
            }
          }
        });
      }
    });
  }

  /**
   * Evaluates whether a given row matches all specified filter conditions
   * @param rowData The data object for the current ro
   * @param filters Array of filter conditions to evaluate
   * @private
   */
  private evalFilter(rowData: Entity, filters: Filter[]): boolean {
    if (!filters) return true;

    return filters.every(filter => {
      const filterFieldValue = getValue(rowData, filter.field.split('.'));

      switch (filter.kind) {
        case FilterKind.NUMBER:
          switch (filter.numberFilter.type) {
            case NumberFilterType.NUMBER_GREATER_THAN_OR_EQUAL:
              return filterFieldValue >= filter.value;
            case NumberFilterType.NUMBER_GREATER_THAN:
              return filterFieldValue > filter.value;
            case NumberFilterType.NUMBER_LESS_THAN_OR_EQUAL:
              return filterFieldValue <= filter.value;
            case NumberFilterType.NUMBER_LESS_THAN:
              return filterFieldValue < filter.value;
            default:
              return true;
          }

        case FilterKind.EQUALS:
          return Array.isArray(filterFieldValue) ?
            Array.isArray(filter.value) &&
            filter.value.length === filterFieldValue.length &&
            filterFieldValue.every(fv => filter.value.includes(fv))
            : filterFieldValue == filter.value;

        case FilterKind.NOT_EQUALS:
          return Array.isArray(filterFieldValue) ?
            !Array.isArray(filter.value) ||
            filter.value.length !== filterFieldValue.length ||
            filterFieldValue.some(fv => !filter.value.includes(fv))
            : filterFieldValue != filter.value;

        case FilterKind.IN: // Array of values
          return filter.value.has(filterFieldValue);

        default:
          return true;
      }
    });
  }

  /**
   * Resets all indicator total values to zero before recalculation.
   * @private
   */
  private clearInitValues(): void {
    this.indicators.forEach(i => i.totalValue = 0);
  }

  /**
   * Resets all indicator filtered values to zero before recalculation.
   * @private
   */
  private clearFilteredValues(): void {
    this.indicators.forEach(i => i.filteredValue = 0);
  }
}
